import {
  CONFIG_OPTION_TOAST_ERROR,
  CONFIG_OPTION_TOAST_NORMAL,
} from "common/toast";
import en from "date-fns/locale/en-US";
import ko from "date-fns/locale/ko";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useTranslation } from "react-i18next";
import "react-quill/dist/quill.snow.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Progress,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";
import UploadImageSingle from "components/Common/UploadImageSingle";
import { UploadProps } from "antd";
import { getAgencyDetail, postAgency, putAgency } from "store/agency/thunk";
import DropdownStatusNew from "components/Common/DropdownStatusNew";
import {
  AGENCY_APP_NAME_OPTION,
  AGENCY_BRANCH_STATUS_OPTION,
  COLOR_AGENCY_BRANCH_STATUS,
} from "helpers/constans";
import DropdownOptionNew from "components/Common/DropdownOptionNew";
import CustomParametersForm from "components/Common/CustomParametersForm";
import { IParams } from "api/types/_params";
import { formatDateStringToKorea } from "helpers/format";
import { unique } from "helpers/validate_yup";

export interface Props {
  id?: string;
  isCopy?: boolean;
  triggerRefresh?: () => void;
  isShowDetail?: boolean;
  setLinkImage?: any;
  setIsOpen?: any;
  setItemToEdit?: any;
}

registerLocale("en", en);
registerLocale("ko", ko);

Yup.addMethod(Yup.array, "unique", unique);

const uploadProps: UploadProps = {
  name: "files",
  accept: "image/png,image/jpeg,image/*",
  beforeUpload: () => false,
  headers: {
    authorization: "authorization-text",
  },
};

const PARAMS_KEY_DEFINE = ['agency', 'campaign'];

const AgencyForm = ({
  id = "",
  triggerRefresh,
  isShowDetail = false,
  setLinkImage,
  setIsOpen,
  setItemToEdit,
}: Props) => {
  const { t, i18n } = useTranslation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [isFirstProgress, setIsFirstProgress] = useState<boolean>(true);

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading((_prev) => true);

      const filterDataParams = values?.params?.filter(
        (item: IParams) => item?.param_key || item?.param_value
      );
      const formatDataParams =
        filterDataParams?.length > 0
          ? filterDataParams?.map((item: IParams, index: number) => ({
              ordering: item?.ordering,
              param_key: item?.param_key,
              param_value: item?.param_value,
              description: item?.description,
              status: item?.status,
            }))
          : [];

      const data = {
        app_name: values?.app_name,
        agency_name: values?.agency_name,
        avatar: values?.avatar,
        contact_name: values?.contact_name,
        email: values?.email,
        phone: values?.phone,
        status: values?.status == 1 ? 1 : 2,
        params: formatDataParams,
        // ...(id && {referral_code: values?.referral_code}),
        // ...(id && {updated_at: values?.updated_at}),
      };

      const response: any = id
        ? await putAgency(id, data)
        : await postAgency(data);

      if (response?.data) {
        setIsLoading((_prev) => false);
        toast(
          `${t("The process has been completed.")}`,
          CONFIG_OPTION_TOAST_NORMAL
        );
        triggerRefresh && triggerRefresh();
      } else {
        setIsLoading((_prev) => false);
        toast(`${response}`, CONFIG_OPTION_TOAST_ERROR);
      }
    } catch (error: any) {
      setIsLoading((_prev) => false);
      toast(`${error?.message || ""}`, CONFIG_OPTION_TOAST_ERROR);
      return error;
    }
  };

  const validationSchema = Yup.object({
    agency_name: Yup.string().required(`${t("Agency Name is required")}`),
    contact_name: Yup.string().required(`${t("Contact Name is required")}`),
    app_name: Yup.string().required(`${t("App Name is required")}`),
    params: Yup.array()
      .of(
        Yup.object().shape({
          param_key: Yup.string().matches(
            /^[A-Za-z]+$/,
            `${t("Only allow characters, no spaces")}`
          ).test("param_key", `${t("The key cannot be 'agency' or 'campaign'")}`, (value) => !PARAMS_KEY_DEFINE.includes(value || '')),
          param_value: Yup.string().when("param_key", {
            is: (value: any) => !!value,
            then: (rule) => {
              return rule.test(
                "param_key",
                `${t("This field is required")}`,
                (target) => !!target?.trim()
              );
            },
          }),
        })
      )
      .unique("Key is duplicated", (a: any) => a.param_key),
    // email: Yup.string().email(`${t("Email Address is invalid")}`).required(`${t("Email Address is required")}`),
    // avatar: Yup.string().required(`${t("Avatar is required")}`),
  });

  const formik = useFormik({
    initialValues: {
      avatar: "",
      agency_name: "",
      contact_name: "",
      app_name: AGENCY_APP_NAME_OPTION[0]?.value,
      phone: "",
      email: "",
      referral_code: "",
      status: 1,
      branch_link_status: null,
      params: [
        {
          id: 1,
          param_key: "",
          param_value: "",
          description: "",
          status: 1,
          ordering: 1,
        },
      ] as IParams[],
      updated_at: "",
      created_at: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const handleSetValueForm = (valueDefault: any) => {
    formik.setValues({
      ...valueDefault,
      updated_at: valueDefault.updated_at
        ? formatDateStringToKorea(valueDefault.updated_at)
        : null,
      created_at: valueDefault.created_at
        ? formatDateStringToKorea(valueDefault.created_at)
        : null,
      params: valueDefault?.agency_params ?? [],
    });
  };

  const handleCallAgencyDetail = async (idItem: string) => {
    try {
      if (!idItem) {
        return;
      }
      setIsLoadingDetail((_prev) => true);
      const resDetail: any = await getAgencyDetail(idItem);

      if (resDetail?.data) {
        handleSetValueForm(resDetail?.data);
        setIsLoadingDetail((_prev) => false);
      }
    } catch (error: any) {
      setIsLoadingDetail((_prev) => false);
      return error;
    }
  };

  const onCloseClick = () => {
    setIsOpen(false);
    setItemToEdit(false);
  };

  useEffect(() => {
    handleCallAgencyDetail(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    document.title = `${t("Create Agency")} - ${t("Agency")} | Baive`;
    document.body.classList.remove("vertical-sidebar-enable");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n?.language]);

  useEffect(() => {
    if (Object.keys(formik.errors).length > 0) {
      setIsFirstProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(formik.errors).length]);

  return (
    <React.Fragment>
      <Modal isOpen={true} centered={true} scrollable={false} size="xl">
        <ModalHeader toggle={isLoading ? () => {} : onCloseClick}>
          {id ? t("Update Agency") : t("Create Agency")}
        </ModalHeader>
        <ModalBody className="py-3 px-4">
          <div className="mb-4">
            <Progress
              value={
                !isFirstProgress || !!id
                  ? 100 - (100 / 3) * Number(Object.keys(formik.errors).length)
                  : 0
              }
              color="primary"
              className="animated-progress progress-sm"
            />
          </div>

          <div className="mt-3">
            <>
              {/* {isLoadingDetail && (
                <div
                  style={{
                    position: "absolute",
                    zIndex: 3,
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                    backgroundColor: "rgb(164 164 164 / 36%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Spinner size="sm" color="primary"></Spinner>
                </div>
              )} */}
              <form onSubmit={formik.handleSubmit}>
                <Row
                  className="modal-custom-scroll"
                  style={{ overflowY: "auto", maxHeight: "68vh" }}
                >
                  {id && (
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label htmlFor="referral_code" className="form-label">
                          {t("Referral Code")}
                        </Label>
                        <Input
                          name="referral_code"
                          type="text"
                          className="form-control"
                          id="referral_code"
                          disabled={true}
                          placeholder={t("Enter your referral code")}
                          value={formik?.values?.referral_code}
                          onChange={(event: any) =>
                            formik.setFieldValue(
                              "referral_code",
                              event?.target?.value
                            )
                          }
                        />
                        {formik.touched.referral_code &&
                        formik.errors.referral_code ? (
                          <div className="text-danger mt-2">
                            {formik.errors.referral_code}
                          </div>
                        ) : null}
                      </div>
                    </Col>
                  )}

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="agencyName" className="form-label">
                        {t("Agency Name")}{" "}
                        <span className="text-danger">*</span>
                      </Label>
                      <Input
                        id="agencyName"
                        name="agency_name"
                        type="text"
                        className="form-control"
                        placeholder={t("Enter your agency name")}
                        value={formik?.values?.agency_name}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "agency_name",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.agency_name &&
                      formik.errors.agency_name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.agency_name}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">
                        {t("App")}
                        <span className="text-danger"> *</span>
                      </Label>

                      <DropdownOptionNew
                        name="app_name"
                        dataList={AGENCY_APP_NAME_OPTION || []}
                        placeholder={`${t("Select App Name")}...`}
                        className="dropdown-status-rounded"
                        classNamePrefix="name-prefix"
                        initialValue={formik?.values?.app_name}
                        onChangeSelect={(event: any) => {
                          formik.setFieldValue("app_name", event?.value);
                        }}
                        isHasOptionAll={false}
                        lstImage={true}
                        disabled={id ? true : false}
                        hideSelectedOptions={false}
                      />
                    </div>
                  </Col>

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="contactName" className="form-label">
                        {t("Contact Name")}{" "}
                        <span className="text-danger"> *</span>
                      </Label>
                      <Input
                        name="contactName"
                        type="text"
                        className="form-control"
                        id="contact_name"
                        placeholder={t("Enter your contact name")}
                        value={formik?.values?.contact_name}
                        onChange={(event: any) =>
                          formik.setFieldValue(
                            "contact_name",
                            event?.target?.value
                          )
                        }
                      />
                      {formik.touched.contact_name &&
                      formik.errors.contact_name ? (
                        <div className="text-danger mt-2">
                          {formik.errors.contact_name}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="phone" className="form-label">
                        {t("Contact Phone")}
                      </Label>
                      <Input
                        name="phone"
                        type="text"
                        className="form-control"
                        id="phone"
                        placeholder={t("Enter your phone number")}
                        value={formik?.values?.phone}
                        onChange={(event: any) =>
                          formik.setFieldValue("phone", event?.target?.value)
                        }
                      />
                      {formik.touched.phone && formik.errors.phone ? (
                        <div className="text-danger mt-2">
                          {formik.errors.phone}
                        </div>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="mb-3">
                      <Label htmlFor="emailInput" className="form-label">
                        {t("Email Address")}
                      </Label>
                      <Input
                        name="email"
                        type="text"
                        className="form-control"
                        id="emailInput"
                        placeholder={t("Enter your email")}
                        value={formik?.values?.email}
                        onChange={(event: any) =>
                          formik.setFieldValue("email", event?.target?.value)
                        }
                      />
                      {formik.touched.email && formik.errors.email ? (
                        <div className="text-danger mt-2">
                          {formik.errors.email}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  {id && (
                    <>
                      <Col xxl={6}>
                        <label className="form-label">
                          {t("Linked Branch IO")}
                        </label>

                        <DropdownStatusNew
                          name="status_branch"
                          dataList={AGENCY_BRANCH_STATUS_OPTION || []}
                          placeholder={`${t("Linked Branch IO")}...`}
                          className="dropdown-status-rounded"
                          classNamePrefix="name-prefix"
                          initialValue={formik.values.branch_link_status}
                          disabled={true}
                          onChangeSelect={(event: any) => {
                            formik.setFieldValue(
                              "branch_link_status",
                              event?.value
                            );
                          }}
                          isHasOptionAll={false}
                          colors={COLOR_AGENCY_BRANCH_STATUS}
                        />

                        {formik.touched.branch_link_status &&
                        formik.errors.branch_link_status ? (
                          <div className="text-danger mt-2">
                            {formik.errors.branch_link_status}
                          </div>
                        ) : null}
                      </Col>

                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="created_at" className="form-label">
                            {t("Created at")}
                          </Label>
                          <Input
                            name="created_at"
                            type="text"
                            className="form-control"
                            id="created_at"
                            disabled={true}
                            value={formik?.values?.created_at}
                            onChange={(event: any) => {
                              console.log(event);
                            }}
                          />
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label htmlFor="updatedAt" className="form-label">
                            {t("Updated at")}
                          </Label>
                          <Input
                            name="email"
                            type="text"
                            className="form-control"
                            id="updatedAt"
                            disabled={true}
                            value={formik?.values?.updated_at}
                            onChange={(event: any) => {
                              console.log(event);
                            }}
                          />
                        </div>
                      </Col>
                    </>
                  )}

                  <Col lg={6}>
                    <div className="mb-3">
                      <Label className="form-label">{t("Status")}</Label>

                      <div>
                        <div
                          className="form-check form-switch form-switch-sm text-center"
                          dir="ltr"
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            name="status"
                            checked={formik?.values?.status == 1 ? true : false}
                            onChange={(event) => {
                              formik.setFieldValue(
                                "status",
                                event?.target?.checked
                              );
                            }}
                          />
                        </div>
                      </div>

                      {formik?.touched?.status && formik?.errors?.status ? (
                        <div className="text-danger mt-2">
                          {/* {formik?.errors?.status} */}
                        </div>
                      ) : null}
                    </div>
                  </Col>

                  <Col xxl={12}>
                    <div className="mb-3">
                      <label className="form-label">{t("Avatar")}</label>

                      <UploadImageSingle
                        data={formik.getFieldProps("avatar")?.value}
                        setLinkImage={setLinkImage}
                        formik={formik}
                        isMultiple={false}
                        uploadProps={uploadProps}
                        formikNameImage={"avatar"}
                      />
                    </div>
                  </Col>

                  <CustomParametersForm formik={formik} />
                </Row>

                <Col lg={12}>
                  <div className="hstack gap-2 justify-content-end mt-3">
                    {!isShowDetail && (
                      <button type="submit" className="btn btn-primary fs-14">
                        {isLoading ? (
                          <Spinner size="sm" className="me-2"></Spinner>
                        ) : !!id ? (
                          <i className="ri-login-circle-line align-bottom me-2 "></i>
                        ) : (
                          <i className="ri-add-fill align-bottom me-2"></i>
                        )}
                        {id
                          ? t("Button Update Agency")
                          : t("Button Create Agency")}
                      </button>
                    )}
                  </div>
                </Col>
              </form>
            </>
          </div>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default AgencyForm;
